import { useAction } from "convex/react";
import { LoaderCircleIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
	type ComposeBodyStyle,
	detectComposeBodyStyle,
	detectLinkColor,
	sanitizeClipboardHtml,
} from "@/lib/sanitize-clipboard-html";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";

function plaintextToHtml(text: string): string {
	const escaped = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
	return `<div style="white-space:pre-wrap;font-family:inherit">${escaped}</div>`;
}

function extractCidRefs(html: string): string[] {
	const out = new Set<string>();
	const re = /src=["']cid:([^"'>]+)["']/gi;
	for (
		let m = re.exec(html);
		m !== null;
		m = re.exec(html)
	) {
		out.add(m[1]);
	}
	return [...out];
}

function substituteCidsWithDataUrls(
	html: string,
	resolved: Record<string, { contentType: string; contentBytesB64: string }>,
): string {
	let out = html;
	for (const [cid, bytes] of Object.entries(resolved)) {
		const dataUrl = `data:${bytes.contentType};base64,${bytes.contentBytesB64}`;
		// Replace every src="cid:<cid>" or src='cid:<cid>' with the data URL.
		const escaped = cid.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		out = out.replace(
			new RegExp(`src=(["'])cid:${escaped}\\1`, "gi"),
			`src=$1${dataUrl}$1`,
		);
	}
	return out;
}

interface SignaturePasteEditorProps {
	value: string;
	onChange: (next: string) => void;
	onComposeBodyStyleDetected?: (style: ComposeBodyStyle | null) => void;
	placeholder?: string;
	className?: string;
}

export function SignaturePasteEditor({
	value,
	onChange,
	onComposeBodyStyleDetected,
	placeholder = "Paste your signature here — copy it from a new email in Outlook (⌘V / Ctrl-V).",
	className,
}: SignaturePasteEditorProps) {
	const ref = useRef<HTMLDivElement>(null);
	const lastInternal = useRef("");
	const [isEmpty, setIsEmpty] = useState(value.trim().length === 0);
	const [isResolving, setIsResolving] = useState(false);
	const resolveCids = useAction(
		api.mail.cidResolver.resolveSignatureCidsFromRecentSent,
	);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (value === lastInternal.current) return;
		el.innerHTML = value;
		lastInternal.current = value;
		setIsEmpty(el.textContent?.trim().length === 0 && !el.querySelector("img"));
	}, [value]);

	function emitChange() {
		const el = ref.current;
		if (!el) return;
		const next = el.innerHTML;
		lastInternal.current = next;
		setIsEmpty(el.textContent?.trim().length === 0 && !el.querySelector("img"));
		onChange(next);
		if (onComposeBodyStyleDetected) {
			if (next.trim().length === 0) {
				onComposeBodyStyleDetected(null);
			} else {
				const doc = new DOMParser().parseFromString(next, "text/html");
				const base = detectComposeBodyStyle(doc);
				if (base) {
					const linkColor = detectLinkColor(doc);
					onComposeBodyStyleDetected(
						linkColor ? { ...base, linkColor } : base,
					);
				} else {
					onComposeBodyStyleDetected(null);
				}
			}
		}
	}

	function insertAtCursor(toInsert: string) {
		if (!ref.current) return;
		const selection = window.getSelection();
		if (
			selection &&
			selection.rangeCount > 0 &&
			ref.current.contains(selection.anchorNode)
		) {
			const range = selection.getRangeAt(0);
			range.deleteContents();
			const fragment = range.createContextualFragment(toInsert);
			const lastNode = fragment.lastChild;
			range.insertNode(fragment);
			if (lastNode) {
				const newRange = document.createRange();
				newRange.setStartAfter(lastNode);
				newRange.collapse(true);
				selection.removeAllRanges();
				selection.addRange(newRange);
			}
		} else {
			ref.current.innerHTML += toInsert;
		}
	}

	async function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
		e.preventDefault();
		const html = e.clipboardData.getData("text/html");
		const text = e.clipboardData.getData("text/plain");
		let toInsert = html
			? sanitizeClipboardHtml(html)
			: text
				? plaintextToHtml(text)
				: "";
		if (!toInsert) return;

		// One Graph round-trip: recovers cid: image bytes + detects compose style from recent sent items.
		const cidRefs = extractCidRefs(toInsert);
		let serverComposeStyle: ComposeBodyStyle | null = null;
		setIsResolving(true);
		try {
			const result = await resolveCids({ cids: cidRefs });
			toInsert = substituteCidsWithDataUrls(toInsert, result.resolvedCids);
			serverComposeStyle = result.composeBodyStyle;
		} catch {
			// Fail quietly: cid refs stay unresolved, images render broken.
		} finally {
			setIsResolving(false);
		}

		insertAtCursor(toInsert);
		emitChange();
		// Server detection beats local DOM walk, which can pick up disclaimer fonts from the signature.
		if (serverComposeStyle && onComposeBodyStyleDetected) {
			onComposeBodyStyleDetected(serverComposeStyle);
		}
	}

	return (
		<div className="relative">
			<div
				ref={ref}
				contentEditable
				onPaste={handlePaste}
				onInput={emitChange}
				suppressContentEditableWarning
				className={cn(
					"min-h-[180px] rounded-md border border-dashed border-input bg-background px-4 py-3 text-sm",
					"focus:outline-none focus:ring-2 focus:ring-ring focus:border-solid",
					"[&_*]:max-w-full [&_img]:inline-block [&_img]:h-auto",
					className,
				)}
			/>
			{isEmpty && !isResolving ? (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-muted-foreground">
					{placeholder}
				</div>
			) : null}
			{isResolving ? (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/80 text-sm text-muted-foreground">
					<LoaderCircleIcon className="mr-2 size-4 animate-spin" />
					Resolving inline images from your sent items…
				</div>
			) : null}
		</div>
	);
}
